import { Copy } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { fmtTime } from "@/lib/api";
import { CheckBadge } from "@/components/doorman/LedgerTable";

const Row = ({ k, v, mono }) => (
  <div className="grid grid-cols-[120px_1fr] gap-3 py-1.5 border-b border-[#222D3D]/60 last:border-0">
    <span className="text-xs text-slate-500">{k}</span>
    <span className={`text-xs text-slate-200 ${mono ? "font-mono break-all" : "break-words"}`}>{v === "" || v == null ? "—" : String(v)}</span>
  </div>
);

const Section = ({ title, children, testId }) => (
  <section className="space-y-2" data-testid={testId}>
    <h3 className="text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">{title}</h3>
    <div className="rounded-md border border-[#222D3D] bg-[#07090E] px-3 py-1">{children}</div>
  </section>
);

export const EntryDetailSheet = ({ entry, onClose }) => {
  const e = entry || {};
  const d = e.details || {};
  const v = e.verification || {};
  const copyJson = () => navigator.clipboard.writeText(JSON.stringify(e, null, 2)).then(() => toast.success("Entry copied as JSON")).catch(() => toast.error("Copy failed"));

  return (
    <Sheet open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-[#0F141C] border-[#222D3D] w-full sm:max-w-xl overflow-y-auto" data-testid="entry-detail-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-slate-100 flex items-center gap-3">#{e.seq} · {e.identity} <CheckBadge status={v.status} testId="entry-detail-check" /></SheetTitle>
          <SheetDescription>{fmtTime(e.arrived_at)}{e.device ? ` · via ${e.device}` : ""}</SheetDescription>
        </SheetHeader>
        <div className="space-y-5 mt-6">
          <Section title="Origin check" testId="entry-detail-verification">
            <Row k="Result" v={{ verified: "Verified — origin matches the operator", failed: "Impersonator — origin does not match", undeclared: "Undeclared — did not name itself", unverifiable: "Unverified — no way to check" }[v.status] || v.status} />
            <Row k="Method" v={v.method} />
            <Row k="Detail" v={v.detail} mono />
          </Section>
          {e.signals?.length > 0 && (
            <Section title="Behaviour signals" testId="entry-detail-signals">
              {e.signals.map((s) => <p key={s} className="text-xs text-sky-300 py-1.5 border-b border-[#222D3D]/60 last:border-0">{s}</p>)}
            </Section>
          )}
          <Section title="Identity" testId="entry-detail-identity">
            <Row k="Agent" v={e.identity} />
            <Row k="Company" v={e.operator} />
            <Row k="Declared" v={e.classification === "undeclared" ? "No — spotted by behaviour" : "Yes — named itself"} />
            {e.user_agent && <Row k="User-agent" v={e.user_agent} mono />}
          </Section>
          {d.direction && (
            <Section title="Network" testId="entry-detail-network">
              <Row k="Direction" v={d.direction === "outbound" ? "Outbound — AI service used from inside the building" : "Inbound — AI agent reaching an on-site system"} />
              <Row k="Network area" v={d.area} />
              <Row k="Destination" v={d.destination || e.site} mono />
              <Row k="Seen via" v={d.protocol ? `${d.protocol.toUpperCase()}${d.port ? ` · port ${d.port}` : ""}` : ""} mono />
              <Row k="Service type" v={d.category} />
            </Section>
          )}
          {(!d.direction || d.method) && <Section title="Request" testId="entry-detail-request">
            <Row k="Method" v={d.method} mono />
            <Row k="Host" v={d.host} mono />
            <Row k="Page" v={d.path || e.site} mono />
            <Row k="Protocol" v={d.http_version} mono />
            {d.status != null && <Row k="Response" v={d.status} mono />}
            <Row k="Rate" v={d.requests_last_minute != null ? `${d.requests_last_minute} requests in the last minute from this source` : ""} />
            <Row k="Source" v={e.source === "auto" ? `detected (${e.logged_by})` : `logged by hand by ${e.logged_by}`} />
          </Section>}
          {d.agent_headers && Object.keys(d.agent_headers).length > 0 && (
            <Section title="Agent identification headers" testId="entry-detail-headers">
              {Object.entries(d.agent_headers).map(([k, val]) => <Row key={k} k={k} v={val} mono />)}
            </Section>
          )}
          <p className="text-[11px] text-slate-500" data-testid="entry-detail-privacy-note">No devices, guests, IP addresses, query strings, cookies or other personal data are collected — only the network area name and the agent. Origin checks run on the device.</p>
          <Section title="Ledger proof" testId="entry-detail-proof">
            <Row k="Entry hash" v={e.hash} mono />
            <Row k="Previous hash" v={e.prev_hash} mono />
            <Row k="Catalog hash" v={e.catalog_hash} mono />
            <Row k="Bitcoin" v={e.anchor_seq ? `included in anchor #${e.anchor_seq}` : "waiting for next anchor"} />
          </Section>
          <button onClick={copyJson} className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1.5" data-testid="entry-detail-copy-json">
            <Copy className="h-3.5 w-3.5" />Copy full entry as JSON
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
